// Light/dark theme toggle.
//
// The theme itself is applied by PlatformWeb.Layouts.theme_bootstrap/1, an
// inline script that runs before the stylesheet so there is no flash of the
// wrong palette. The switch is the `theme-dark` class rather than Tailwind's
// `dark` — see assets/tailwind.config.js for why that variant is disabled. This
// hook owns everything after first paint: reflecting current state in the
// button, reacting to clicks, and keeping other tabs and the OS setting in
// step.
//
// Preference model — three states:
//   "light" / "dark"  explicit user choice
//   "system"          follow the OS via prefers-color-scheme (the default)
//
// Cycling goes light -> dark -> system, so a user can always get back to
// "just do what my OS does" without clearing storage by hand.
//
// The choice is written to two places. `localStorage` is what the pre-paint
// bootstrap reads, so it has to be synchronous and local. It is also PUT to the
// server, which is what makes the preference follow someone to another browser
// or machine — on the next page load the layout renders it directly into the
// markup and localStorage is re-seeded from it.

const STORAGE_KEY = "tms:theme";
const MODES = ["light", "dark", "system"];

const systemPrefersDark = () =>
  window.matchMedia("(prefers-color-scheme: dark)").matches;

const readMode = () => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch (_e) {
    return "system";
  }
};

const writeMode = (mode) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch (_e) {
    // Private browsing or a full quota — the choice applies to this page only.
  }

  persistMode(mode);
};

// Fire-and-forget: the theme is already applied and cached locally, so a failed
// write costs the user nothing until their next session on another device.
// Interrupting them over it would be worse than the loss.
const persistMode = (mode) => {
  const token = document
    .querySelector("meta[name='csrf-token']")
    ?.getAttribute("content");

  if (!token) return;

  window
    .fetch("/users/theme", {
      method: "PUT",
      headers: {
        "content-type": "application/json",
        "x-csrf-token": token,
      },
      body: JSON.stringify({ theme: mode }),
      credentials: "same-origin",
      keepalive: true,
    })
    .catch(() => {
      // Offline, or signed out in another tab. localStorage still holds it.
    });
};

const resolve = (mode) =>
  mode === "system" ? (systemPrefersDark() ? "dark" : "light") : mode;

// The transition class is added only around an actual flip. Leaving it on
// permanently would put a 180ms colour transition on every element in the app,
// which makes hover states feel laggy.
const applyTheme = (theme, {animate = false} = {}) => {
  const root = document.documentElement;

  if (animate) {
    root.classList.add("theme-transition");
    window.clearTimeout(applyTheme._timer);
    applyTheme._timer = window.setTimeout(
      () => root.classList.remove("theme-transition"),
      200
    );
  }

  root.classList.toggle("theme-dark", theme === "dark");
  root.dataset.theme = theme;

  // Grids, charts and maps that cache colours at construction time listen for
  // this rather than polling the class list.
  window.dispatchEvent(new CustomEvent("tms:theme-changed", {detail: {theme}}));
};

const ThemeToggle = {
  mounted() {
    this.mode = readMode();
    this.render();

    this.onClick = () => {
      this.mode = MODES[(MODES.indexOf(this.mode) + 1) % MODES.length];
      writeMode(this.mode);
      applyTheme(resolve(this.mode), {animate: true});
      this.render();
    };
    this.el.addEventListener("click", this.onClick);

    // Follow the OS only while the user has not made an explicit choice.
    this.media = window.matchMedia("(prefers-color-scheme: dark)");
    this.onMedia = () => {
      if (this.mode !== "system") return;
      applyTheme(resolve(this.mode), {animate: true});
      this.render();
    };
    this.media.addEventListener("change", this.onMedia);

    // A second tab changing the theme writes to the same key; `storage` fires
    // in every OTHER tab, which is exactly the set that needs to catch up.
    this.onStorage = (event) => {
      if (event.key !== STORAGE_KEY && event.key !== null) return;
      this.mode = readMode();
      applyTheme(resolve(this.mode), {animate: true});
      this.render();
    };
    window.addEventListener("storage", this.onStorage);
  },

  // LiveView patches can replace the button's contents; re-assert them.
  updated() {
    this.render();
  },

  render() {
    const theme = resolve(this.mode);
    const label =
      this.mode === "system"
        ? `Theme: follow system (${theme})`
        : `Theme: ${this.mode}`;

    // Which icon shows is decided in CSS from `data-theme-mode` on <html>
    // (see theme.css) — the same attribute the pre-paint bootstrap sets, so
    // the button never renders the wrong icon and then corrects itself.
    document.documentElement.dataset.themeMode = this.mode;

    this.el.setAttribute("aria-label", label);
    this.el.setAttribute("title", `${label} — click to change`);
  },

  destroyed() {
    this.el.removeEventListener("click", this.onClick);
    this.media.removeEventListener("change", this.onMedia);
    window.removeEventListener("storage", this.onStorage);
  }
};

export default ThemeToggle;
export {applyTheme, resolve, readMode};
